// ##############################
// // // Consultation view styles
// #############################

import {
  dangerColor,
  grayColor,
  kickAmaOrange,
  kickBlue,
  kickLightBlue,
  kickNavy,
  kickRed,
  cardSubtitle as mdCardSubtitle,
  cardTitle as mdCardTitle,
  primaryBoxShadow,
  primaryColor,
  roseColor,
} from "~/shared/styles/baseStyles.js";

// Checkbox/radio/switch styles (extracted from material-dashboard)
const customCheckboxRadioSwitch = {
  checkRoot: {
    padding: "14px",
  },
  radioRoot: {
    padding: "16px",
  },
  checkboxAndRadio: {
    position: "relative",
    display: "block",
    marginTop: "10px",
    marginBottom: "10px",
  },
  checkboxAndRadioHorizontal: {
    position: "relative",
    display: "block",
    "&:first-child": {
      marginTop: "10px",
    },
    "&:not(:first-child)": {
      marginTop: "-14px",
    },
    marginTop: "0",
    marginBottom: "0",
  },
  checked: {
    color: primaryColor + "!important",
  },
  checkedIcon: {
    width: "20px",
    height: "20px",
    border: "1px solid rgba(0, 0, 0, .54)",
    borderRadius: "3px",
  },
  uncheckedIcon: {
    width: "0px",
    height: "0px",
    padding: "9px",
    border: "1px solid rgba(0, 0, 0, .54)",
    borderRadius: "3px",
  },
  // --- Consent gate error state ------------------------------------------
  //
  // Used by the two consent checkboxes in signup (Terms/Privacy, then
  // Telehealth Consent/NPP). Scoped to those on purpose -- these classes are
  // additive rather than edits to checkedIcon/uncheckedIcon, which are shared
  // by every checkbox in the consultation flow.
  //
  // The problem being fixed: on a failed submit only the helper text turned
  // red. The box kept its resting rgba(0,0,0,.54) border, so the message read
  // "click the checkbox" while pointing at a control that looked untouched.
  //
  // consentRowError tints the whole row so the error reads as a region rather
  // than a 20px detail, and nudges once. The nudge earns its place because the
  // submit button sits BELOW the checkbox: on a phone the thing that changed is
  // often above the fold you are looking at when you tap, and movement is what
  // brings the eye back. It is disabled under prefers-reduced-motion.
  consentRow: {
    display: "block",
    // Capped and centred as a block, but the CONTENT is left-aligned. Centring
    // the text meant a wrapped label centred its second line under the first,
    // which reads as broken rather than as one sentence.
    // Hugs its content and centres as a block, so the checkbox+label group
    // shares a centre line with the submit button below it. A fixed-width block
    // would centre the BOX while leaving the content inside it off-centre, which
    // is what made the two look misaligned.
    width: "fit-content",
    maxWidth: "min(440px, 100%)",
    margin: "16px auto 0",
    // Content stays left-aligned inside that block: centring the text made a
    // wrapped label centre its second line under the first.
    textAlign: "left",
    border: "1px solid transparent",
    borderRadius: "9px",
    padding: "4px 10px 10px",
    // The whole panel toggles, not just the box. See the onClick in the
    // components: the label already covers its own row natively, and the panel
    // handles the padding and the helper line so there is no dead area inside a
    // control that looks like one target.
    cursor: "pointer",
    transition: "background-color .15s ease, border-color .15s ease",
  },
  consentRowError: {
    backgroundColor: "rgba(242, 89, 48, .08)",
    borderColor: dangerColor,
    animation: "$consentNudge .38s ease",
  },
  "@keyframes consentNudge": {
    "0%, 100%": { transform: "translateX(0)" },
    "22%": { transform: "translateX(-4px)" },
    "55%": { transform: "translateX(4px)" },
    "80%": { transform: "translateX(-2px)" },
  },
  "@media (prefers-reduced-motion: reduce)": {
    consentRowError: { animation: "none" },
    consentRow: { transition: "none" },
  },
  // 24px rather than the shared 20px: this is the last control before a visit
  // starts, and 20px is under every touch-target guideline going.
  consentUncheckedIcon: {
    width: "0px",
    height: "0px",
    padding: "11px",
    border: "1px solid rgba(0, 0, 0, .54)",
    borderRadius: "5px",
  },
  consentCheckedIcon: {
    width: "24px",
    height: "24px",
    border: "1px solid rgba(0, 0, 0, .54)",
    borderRadius: "5px",
  },
  // The shared checkRoot pads 14px on every side, which costs 28px of horizontal
  // room and pushed "I agree to Terms of Use & Privacy Policy" onto a second line at
  // iPhone-width. Narrowed horizontally only.
  //
  // Safe because the WHOLE panel toggles (see the onClick in the components), so the
  // tap target is the row, not the box. 10px vertical keeps the row itself a
  // comfortable height; the box stays 24px so it is no smaller to look at.
  consentCheckRoot: {
    padding: "10px 6px",
  },
  consentIconError: {
    borderColor: dangerColor,
    borderWidth: "2px",
    boxShadow: "0 0 0 3px rgba(242, 89, 48, .18)",
  },
  // Keeps a document name whole. If the label does wrap on a very narrow screen it
  // must break BETWEEN phrases -- "Privacy / Policy" split across two lines reads as
  // a rendering fault rather than as one link.
  consentLabelLink: {
    whiteSpace: "nowrap",
  },
  consentLabelError: {
    color: dangerColor,
    fontWeight: 500,
    // The links carry their own colour, so without this the label turns red
    // around two anchors that stay purple and the row looks half-styled.
    "& a": {
      color: "inherit",
    },
  },
  // The helper sits INSIDE the panel. It used to sit outside with a negative
  // margin-top, which pulled it up through the panel's bottom border and drew
  // the border straight through the text.
  consentHelper: {
    // Lines up under the label rather than the checkbox: 6px consentCheckRoot
    // padding + 24px box + 6px padding.
    marginLeft: "36px",
    marginTop: "2px",
    // Clicks fall through to the panel, so the message is part of the target
    // rather than a hole in the middle of it.
    pointerEvents: "none",
  },
  disabledCheckboxAndRadio: {
    "& $checkedIcon,& $uncheckedIcon,& $radioChecked,& $radioUnchecked": {
      borderColor: "#000000",
      opacity: "0.26",
      color: "#000000",
    },
  },
  label: {
    cursor: "pointer",
    paddingLeft: "0",
    color: "#AAAAAA",
    fontSize: "14px",
    lineHeight: "1.428571429",
    fontWeight: "400",
    display: "inline-flex",
    transition: "0.3s ease all",
  },
  labelHorizontal: {
    color: "rgba(0, 0, 0, 0.26)",
    cursor: "pointer",
    display: "inline-flex",
    fontSize: "14px",
    lineHeight: "1.428571429",
    fontWeight: "400",
    paddingTop: "39px",
    marginRight: "0",
    "@media (min-width: 992px)": {
      float: "right",
    },
  },
  labelHorizontalRadioCheckbox: {
    paddingTop: "22px",
  },
  labelLeftHorizontal: {
    color: "rgba(0, 0, 0, 0.26)",
    cursor: "pointer",
    display: "inline-flex",
    fontSize: "14px",
    lineHeight: "1.428571429",
    fontWeight: "400",
    paddingTop: "22px",
    marginRight: "0",
  },
  labelError: {
    color: dangerColor,
  },
  radio: {
    color: primaryColor + "!important",
  },
  radioChecked: {
    width: "16px",
    height: "16px",
    border: "1px solid " + primaryColor,
    borderRadius: "50%",
  },
  radioUnchecked: {
    width: "0px",
    height: "0px",
    padding: "7px",
    border: "1px solid rgba(0, 0, 0, .54)",
    borderRadius: "50%",
  },
  inlineChecks: {
    marginTop: "8px",
  },
  iconCheckbox: {
    height: "116px",
    width: "116px",
    color: grayColor,
    padding: "0",
    margin: "0 auto 20px",
    "& > span:first-child": {
      borderWidth: "4px",
      borderStyle: "solid",
      borderColor: "#CCCCCC",
      textAlign: "center",
      verticalAlign: "middle",
      borderRadius: "50%",
      color: "inherit",
      transition: "all 0.2s",
    },
    "&:hover": {
      color: roseColor,
      "& > span:first-child": {
        borderColor: roseColor,
      },
    },
  },
  iconCheckboxChecked: {
    color: roseColor,
    "& > span:first-child": {
      borderColor: roseColor,
    },
  },
  iconCheckboxIcon: {
    fontSize: "40px",
    lineHeight: "111px",
  },
  switchBase: {
    color: primaryColor + "!important",
  },
  switchIcon: {
    boxShadow: "0 1px 3px 1px rgba(0, 0, 0, 0.4)",
    color: "#FFFFFF !important",
    border: "1px solid rgba(0, 0, 0, .54)",
    transform: "translateX(-4px)!important",
  },
  switchIconChecked: {
    borderColor: "#9c27b0",
    transform: "translateX(0px)!important",
  },
  switchBar: {
    width: "30px",
    height: "15px",
    backgroundColor: "rgb(80, 80, 80)",
    borderRadius: "15px",
    opacity: "0.7!important",
  },
  switchChecked: {
    "& + $switchBar": {
      backgroundColor: "rgba(156, 39, 176, 1) !important",
    },
  },
};

// Select styles (extracted from material-dashboard)
const selectStyles = {
  select: {
    padding: "12px 0 7px",
    fontSize: ".75rem",
    fontWeight: "400",
    lineHeight: "1.42857",
    textDecoration: "none",
    textTransform: "uppercase",
    color: "#3C4858",
    letterSpacing: "0",
    "&:focus": {
      backgroundColor: "transparent",
    },
    "&[aria-owns] + input + svg": {
      transform: "rotate(180deg)",
    },
    "& + input + svg": {
      transition: "all 300ms linear",
    },
  },
  selectFormControl: {
    margin: "10px 1px 10px 0px !important",
    "& > div": {
      "&:before": {
        borderBottomWidth: "1px !important",
        borderBottomColor: "#D2D2D2 !important",
      },
      "&:after": {
        borderBottomColor: primaryColor + "!important",
      },
    },
  },
  selectLabel: {
    fontSize: "12px",
    textTransform: "uppercase",
    color: "#3C4858 !important",
    top: "8px",
  },
  selectMenu: {
    "& > div > ul": {
      border: "0",
      padding: "5px 0",
      margin: "0",
      boxShadow: "none",
      minWidth: "100%",
      borderRadius: "4px",
      boxSizing: "border-box",
      display: "block",
      fontSize: "14px",
      textAlign: "left",
      listStyle: "none",
      backgroundColor: "#fff",
      backgroundClip: "padding-box",
    },
    "& $selectPaper $selectMenuItemSelectedMultiple": {
      backgroundColor: "inherit",
    },
    "& > div + div": {
      maxHeight: "266px !important",
    },
  },
  selectMenuItem: {
    fontSize: "13px",
    padding: "10px 20px",
    margin: "0 5px",
    borderRadius: "2px",
    transition: "all 150ms linear",
    display: "block",
    clear: "both",
    fontWeight: "400",
    lineHeight: "2",
    whiteSpace: "nowrap",
    color: "#333",
    paddingRight: "30px",
    "&:hover": {
      backgroundColor: primaryColor,
      color: "#FFFFFF",
      ...primaryBoxShadow,
    },
  },
  selectMenuItemSelected: {
    backgroundColor: primaryColor + "!important",
    color: "#FFFFFF",
  },
  selectMenuItemSelectedMultiple: {
    backgroundColor: "transparent !important",
    "&:hover": {
      backgroundColor: primaryColor + "!important",
      color: "#FFFFFF",
      ...primaryBoxShadow,
      "&:after": {
        color: "#FFFFFF",
      },
    },
    "&:after": {
      top: "16px",
      right: "12px",
      width: "12px",
      height: "5px",
      borderLeft: "2px solid currentColor",
      transform: "rotate(-45deg)",
      opacity: "1",
      color: "#3c4858",
      position: "absolute",
      content: "''",
      borderBottom: "2px solid currentColor",
      transition: "opacity 90ms cubic-bezier(0,0,.2,.1)",
    },
  },
  selectPaper: {
    boxSizing: "borderBox",
    borderRadius: "4px",
    padding: "0",
    minWidth: "100%",
    display: "block",
    border: "0",
    boxShadow: "0 2px 5px 0 rgba(0, 0, 0, 0.26)",
    backgroundClip: "padding-box",
    margin: "2px 0 0",
    fontSize: "14px",
    textAlign: "left",
    listStyle: "none",
    backgroundColor: "transparent",
    maxHeight: "266px",
  },
};

export const VISIT_MAX_WIDTH = "800px";

const consultationStyle = (theme) => ({
  ...customCheckboxRadioSwitch,
  ...selectStyles,
  cardTitle: {
    ...mdCardTitle,
    fontFamily: "Spectral-Bold",
    marginBottom: 0,
    lineHeight: "1.25",
    [theme.breakpoints.down("md")]: { fontSize: "30px" },
    color: kickBlue,
  },
  cardSubtitle: {
    ...mdCardSubtitle,
  },
  containerWithSpacing: {
    borderTop: "1px solid #ddd",
    paddingTop: "20px",
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    overflowX: "hidden",
    "& > div": {
      maxWidth: "100%",
      boxSizing: "border-box",
      margin: "0",
    },
    [theme.breakpoints.down("md")]: {
      paddingTop: "0",
    },
  },
  container: {
    position: "relative",
    zIndex: "3",
  },
  cardSignup: {
    display: "flex",
    flexDirection: "column",
    borderRadius: "6px",
    boxShadow:
      "0 16px 24px 2px rgba(0, 0, 0, 0.14), 0 6px 30px 5px rgba(0, 0, 0, 0.12), 0 8px 10px -5px rgba(0, 0, 0, 0.2)",
    marginBottom: "100px",
    marginTop: "30px",
    padding: "0px 0px 40px",
    [theme.breakpoints.up("sm")]: {
      paddingTop: "20px",
    },
  },
  signupButton: {
    marginTop: "6px",
    width: "100%",
    maxWidth: "300px",
    backgroundColor: kickAmaOrange,
    color: kickNavy,
    fontSize: "18px",
    fontWeight: "medium",
    textTransform: "unset",
  },
  center: {
    textAlign: "center",
  },
  right: {
    textAlign: "right",
  },
  left: {
    textAlign: "left",
  },
  form: {
    position: "relative",
  },
  socialTitle: {
    fontSize: "18px",
  },
  inputAdornment: {
    marginRight: "18px",
    position: "relative",
  },
  inputAdornmentIcon: {
    color: "#555",
  },
  customFormControlClasses: {
    margin: "0 12px",
  },
  checkboxLabelControl: {
    margin: "0",
  },
  checkboxLabel: {
    marginLeft: "6px",
    fontFamily: "BasisGrotesquePro-Regular",
    fontSize: "16px",
    lineHeight: "22px",
    color: "#1E2347",
  },
  radioLabel: {
    marginLeft: "6px",
    fontFamily: "BasisGrotesquePro-Regular",
    fontSize: "16px",
    lineHeight: "22px",
    color: "#1E2347",
  },

  signupSubHeading: {
    textAlign: "center",
    color: kickNavy,

    padding: "0 80px",

    [theme.breakpoints.down("md")]: { padding: "0" },
  },

  signupComingSoonTitle: {
    alignSelf: "center",
    textAlign: "center",
    margin: "40px auto",
    [theme.breakpoints.up("xs")]: {
      alignItems: "center",
    },
    [theme.breakpoints.up("sm")]: { width: "50%" },
  },

  // Sizing for the product <img> on the select-product screens. Keep the <img>
  // inside this wrapper rather than making it a direct flex item of
  // selectBBProductWrapper: WebKit (Safari / iOS) resolves `height: fit-content`
  // on a replaced element to the PNG's natural pixel height, and in a column
  // flexbox it derives a percentage-constrained image's height from the wrong
  // width — both render the image stretched. A plain block wrapper sidesteps
  // both, and objectFit: contain guarantees the painted pixels keep their
  // aspect ratio even if a layout engine still computes the box wrong.
  selectBBProductImageWrap: {
    flex: "0 0 auto",
    maxWidth: "100%",
    "& > img": {
      display: "block",
      width: "426px",
      maxWidth: "100%",
      height: "auto",
      objectFit: "contain",
    },
  },
  selectBBProductWrapper: {
    [theme.breakpoints.down("sm")]: {
      padding: "30px",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      "& > $selectBBProductImageWrap > img": {
        left: "20px",
      },
      "& > $selectBBProductImageWrap > .imgTwoPack": {
        left: "50px",
      },
    },
    [theme.breakpoints.up("md")]: {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      paddingLeft: "80px",
      paddingTop: "40px",
      paddingBottom: "30px",
      "& > $selectBBProductImageWrap": {
        maxWidth: "50%",
      },
    },
  },

  selectFlavoredProductWrapper: {
    display: "flex",
    "& img": {
      width: "120px",
      height: "95px",
      objectFit: "contain",
    },
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "center",
      padding: "20px",
      "& > img": {
        maxWidth: "100%",
        marginBottom: "10px",
      },
    },
    [theme.breakpoints.up("sm")]: {
      flexDirection: "row",
      alignItems: "center",
      paddingLeft: "80px",
      paddingTop: "20px",
      paddingBottom: "20px",
      "& > img": {
        maxWidth: "50%",
      },
    },
  },

  // Experiment product-flavor-photos, test arm: the row around a ProductPhoto. A
  // separate class rather than a modifier on selectFlavoredProductWrapper because
  // that class's "& img" rule would size ProductPhoto's nested <img> to 120x95.
  // When the experiment ends, keep the winner's rules under the original name.
  selectFlavoredProductWrapperReframed: {
    display: "flex",
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "center",
      padding: "28px 20px",
    },
    [theme.breakpoints.up("sm")]: {
      flexDirection: "row",
      alignItems: "center",
      paddingLeft: "80px",
      paddingRight: "48px",
      paddingTop: "36px",
      paddingBottom: "36px",
    },
  },

  selectBBProductTwoPackWrapper: {
    justifyContent: "left",

    [theme.breakpoints.down("sm")]: {
      textAlign: "center",
      justifyContent: "center",
      fontSize: "16px",
    },

    [theme.breakpoints.up("md")]: {
      marginLeft: "20px",
      paddingRight: "10px",
    },

    "& ul": {
      textAlign: "left",
      [theme.breakpoints.up("md")]: {
        marginLeft: "-30px",
      },
    },
  },
  selectBBProductImageTitle: {
    textAlign: "left",
    fontSize: "20px",

    [theme.breakpoints.down("sm")]: {
      textAlign: "center",
      marginTop: "10px",
      marginLeft: 0,
    },
    [theme.breakpoints.up("md")]: {
      alignSelf: "center",
      bottom: "100px",
    },
  },
  selectFlavorImageTitleWrapper: {
    "& h4": {
      marginTop: "0",
      marginBottom: "0",
    },
    [theme.breakpoints.up("sm")]: {
      marginLeft: "20px",
    },
    [theme.breakpoints.down("sm")]: {
      "& p": {
        textAlign: "center",
      },
    },
  },
  selectFlavorImageTitle: {
    textAlign: "left",
    fontSize: "20px",
    [theme.breakpoints.down("sm")]: {
      textAlign: "center",
    },
  },
  selectBBProductImageTitleLower: {
    textAlign: "left",
    fontSize: "20px",

    [theme.breakpoints.down("sm")]: {
      textAlign: "center",
      marginTop: "10px",
      marginLeft: 0,
    },
    [theme.breakpoints.up("md")]: {
      alignSelf: "center",
      bottom: "100px",
      marginLeft: "-40px",
      paddingRight: "10px",
    },
  },

  // Select Sleep Product Page
  selectSleepProductPlansWrapper: {
    [theme.breakpoints.up("md")]: {
      display: "flex",
      flexDirection: "row",
    },
  },
  selectSleepProductWrapper: {
    [theme.breakpoints.up("xs")]: {
      padding: "30px",
    },
    [theme.breakpoints.up("md")]: {
      display: "flex",
      flexDirection: "row",
      paddingLeft: "80px",
      paddingTop: "40px",
      paddingBottom: "30px",
    },
  },
  selectSleepProductButton: {
    display: "block",
    backgroundColor: "#F25930",
    margin: "4px auto 30px auto",
    width: "200px",
    fontSize: "15px",
    fontWeight: "medium",
    textTransform: "unset",
  },

  // Payment page
  paymentCardBody: {
    [theme.breakpoints.down("sm")]: {
      display: "flex",
      flexDirection: "column-reverse",
      padding: "0 2px",
    },
    [theme.breakpoints.up("md")]: {
      display: "flex",
      flexDirection: "row",
      padding: "0 2px",
    },
  },
  paymentForm: {
    display: "flex",
    justifyContent: "center",
    flexDirection: "column",
    maxWidth: VISIT_MAX_WIDTH,
    marginBottom: "24px",

    [theme.breakpoints.down("sm")]: {
      alignItems: "center",
    },
  },
  paymentHeading: {
    fontSize: "16px",
    fontFamily: "BasisGrotesquePro-Regular",
    marginBottom: "12px",
    paddingTop: "2px",
    fontWeight: "bold",
  },
  paymentHeadingDiscount: {
    color: "#f25930",
  },
  paymentShippingAddressHeading: {
    fontSize: "20px",
    fontFamily: "Spectral",
    marginBottom: "20px",

    [theme.breakpoints.up("md")]: {
      margin: "20px 0",
    },
    [theme.breakpoints.down("sm")]: {
      alignSelf: "flex-start",
      marginTop: "20px",
      marginBottom: "0px",
    },
  },
  confirmAndPayButton: {
    display: "block",
    backgroundColor: "#F25930",
    margin: "40px auto 10px",
    minWidth: "300px",
    borderRadius: "4px",
    fontFamily: "BasisGrotesquePro-Regular",
    textTransform: "none",
    fontSize: "14px",
  },
  stripePaymentRequestButtonBlock: {
    width: "300px",
    margin: "0 auto 40px",
    display: "flex",
    flexDirection: "column",
  },
  paymentTotal: {
    fontSize: "22px",
    fontWeight: "bold",
    marginTop: "20px",
  },
  paymentOrderSummary: {
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    alignItems: "center",

    [theme.breakpoints.down("sm")]: {
      paddingTop: "10px",
      paddingBottom: "10px",
    },
  },
  paymentOrderSummaryImageBetaBlockers: {
    marginTop: "30px",
    marginLeft: "130px",
  },
  paymentOrderSummaryImageSleep: {
    display: "none",

    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },
  paymentOrderSummarySection: {
    display: "flex",
    flexDirection: "row",
    width: "100%",
    justifyContent: "space-between",
  },
  paymentOrderSummaryTitles: {
    display: "flex",
    flexDirection: "column",

    [theme.breakpoints.up("md")]: {
      display: "flex",
      alignItems: "flex-start",
    },
  },
  paymentOrderSummaryPrices: {
    display: "flex",
    flexDirection: "column",
    textAlign: "right",
  },
  paymentGroupPaymentDisclaimer: {
    marginTop: "32px",
    padding: "14px 28px",
    backgroundColor: kickLightBlue,
    alignSelf: "center",
  },
  paymentOrderAdditionalQuestion: {
    alignSelf: "center",
    marginTop: "-10px",
    marginBottom: "28px",
  },
  paymentShippingForm: {
    marginBottom: "40px",
  },
  paymentCardForm: {
    [theme.breakpoints.down("sm")]: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      marginTop: "20px",
    },
    [theme.breakpoints.up("md")]: {
      width: "75%",
      marginTop: "30px",
      marginLeft: "28px",
    },
  },
  paymentOrderConfirmation: {
    display: "flex",
    flexDirection: "column",
    marginTop: "22px",

    [theme.breakpoints.down("sm")]: { alignItems: "center" },

    [theme.breakpoints.up("md")]: {
      width: "75%",
      paddingLeft: "20px",
      paddingTop: "20px",
    },
  },
  label: {
    color: kickNavy,
    fontSize: "14px",
    marginBottom: "2px",
    marginTop: "4px",
    display: "block",
  },
  textField: {
    marginTop: "16px",
  },
  gridItemNoPadding: {
    [theme.breakpoints.down("sm")]: {
      padding: "0 !important",
    },
  },
  orderDetailsProductImage: {
    height: "60px",
  },
  subtitle: {
    fontSize: "24px",
    fontFamily: "Spectral-Bold",
    color: kickBlue,
    fontWeight: "bold",
  },
  checkoutProgressMobile: {
    display: "none",
    [theme.breakpoints.down("sm")]: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      borderBottom: "1px solid #ddd",
      padding: "0 15px",
    },
  },
  checkoutProgressDesktop: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    marginBottom: "20px",
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },
  signupTrustbox: {
    marginBottom: "10px",
    left: "6px !important",
  },
  paymentCompleteCongrats: {
    width: "100%",
    maxWidth: VISIT_MAX_WIDTH,
    padding: "64px",
    display: "flex",
    justifyContent: "center",
    flexDirection: "column",
    alignItems: "center",
    borderBottom: "1px solid #ddd",
    marginBottom: "16px",
    [theme.breakpoints.down("sm")]: {
      padding: "40px",
    },
  },
  priceOption: {
    display: "flex",
    justifyContent: "space-between",
    padding: "16px 8px",
    color: kickNavy,
  },
  upsellPriceOption: {
    display: "flex",
    justifyContent: "space-between",
    padding: "16px 8px",
    color: kickNavy,
    borderTop: "2px solid #e0e0e0",
    alignItems: "flex-start",
    marginTop: "10px",
  },
  priceTagLabel: {
    display: "flex",
    alignItems: "center",
    borderRadius: "10px",
    padding: "2px 8px",
    fontSize: "10px",
    fontWeight: "bold",
    margin: "4px 0 2px",
  },
  labelSubtext: {
    fontSize: "13px",
    color: kickRed,
    marginTop: "4px",
    fontWeight: "bold",
  },
  upgradeBtn: {
    fontSize: "14px",
    backgroundColor: "#ffa41c",
    color: kickNavy,
    display: "block",
    margin: "20px auto 16px",
    minWidth: "200px",
    textTransform: "unset",
  },
});

export default consultationStyle;
